import { EActivityType, ETrackingEventType } from '@constants';
import {
  IActivity,
  IActivityTrack,
  ICreateProblemReport,
  ISection,
  ISubscription,
  ITrackingUpdate
} from '@sdk/contracts';
import { message } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useActivityNavigation } from '../activity-track/useActivityNavigation';
import { useActivityUIState } from '../activity-track/useActivityUIState';
import { usePlanificationNavigation } from '../activity-track/usePlanificationNavigation';

import { useTrackingEvents } from '@/hooks';
import {
  useMutateMySubscriptions,
  useMutateProblemReports,
  useMySubscriptionDetail,
  useMyPlanification
} from '@/store';
import { getActivityTrace, getNextActivityTrack } from '@/utils';

type ICurrentCourseState = {
  activity?: IActivity;
  section?: ISection;
  index: number;
};

export const useActivityTrack = () => {
  const {
    isRecapModalOpen,
    setIsRecapModalOpen,
    showActivityReview,
    setShowActivityReview,
    showNextActivityShortcut,
    setShowNextActivityShortcut
  } = useActivityUIState();
  const { isLoading, subscription } = useMySubscriptionDetail();
  const { planification, sortedPlanificationActivitiesByDate } = useMyPlanification();
  const { isWorking, updateActivityTrack } = useMutateMySubscriptions();
  const { reportProblem } = useMutateProblemReports();
  const [searchParams] = useSearchParams();
  const currentActivityId = searchParams.get('activity');
  const { t } = useTranslation();
  const { trackEvent } = useTrackingEvents();

  const updateActivityTracking = (trackingData: ITrackingUpdate, onSuccess?: () => void) => {
    if (!subscription) return;

    const metadata = getActivityTrace(subscription, trackingData.activity);

    updateActivityTrack(
      {
        subscriptionId: subscription._id,
        activityTrack: { ...metadata, ...trackingData }
      },
      {
        onSuccess: () => onSuccess?.()
      }
    );
  };

  const setActivityAsCompleted = (activityId?: string) => {
    const { currentActivity } = subscription || {};
    setShowActivityReview(true);

    const _id = activityId ? activityId : currentActivityId ?? currentActivity;
    if (!_id || !subscription) return;

    const { section: sectionId } = getActivityTrace(subscription, _id);

    const { isCompleted } = getActivityTrackById(_id);
    if (isCompleted) return;

    updateActivityTracking(
      {
        activity: _id,
        isCompleted: true
      },
      () => {
        const nextActivityTrack = getNextActivityTrack(subscription, activityId);
        if (!nextActivityTrack) return;
        const { activity: { type } = {} } = getCurrentActivity();
        if (!type) return;
        if (type !== EActivityType.VIDEO) setShowNextActivityShortcut(true);
        updateActivityTracking(nextActivityTrack);
        trackEvent({
          eventType: ETrackingEventType.ACTIVITY_COMPLETED,
          eventData: {
            subscriptionId: subscription?._id,
            courseId: subscription?.course?._id,
            sectionId
          }
        });

        if (!planification) return;
        const planificationItem = planification?.items?.find(
          item =>
            item.activity === _id &&
            item.subscription === subscription?._id &&
            planification?.active
        );

        if (planificationItem) {
          trackEvent({
            eventType: ETrackingEventType.PLANIFICATION_ACTIVITY_COMPLETED,
            eventData: {
              subscriptionId: subscription?._id,
              courseId: subscription?.course?._id,
              sectionId,
              activityId: _id
            }
          });
        }
      }
    );
  };

  const setActivityAsIncomplete = () => {
    const { currentActivity } = subscription || {};

    const activity = currentActivityId ?? currentActivity;
    if (!activity) return;

    updateActivityTracking({
      activity,
      isCompleted: false
    });
  };

  const getActivityTrackById = (activity?: string) => {
    const { activityTracking = [] } = subscription || {};
    const currentActivityTrack = activityTracking.find(track => track.activity === activity);

    return {
      ...currentActivityTrack,
      activity: activity ?? ''
    };
  };

  const setActivityReview = (review: IActivityTrack['review'], onSuccess?: () => void) => {
    const { currentActivity } = subscription || {};

    const activity = currentActivityId ?? currentActivity;
    if (!activity) return;

    updateActivityTracking(
      {
        activity,
        review
      },
      () => {
        onSuccess?.();
        message.success(t('THANKS_FOR_REVIEWING'));
      }
    );
  };

  const sendActivityProblemReport = (
    problemReport: ICreateProblemReport,
    onSuccess: () => void
  ) => {
    reportProblem(problemReport, {
      onSuccess
    });
  };

  const getTimeTracking = (activityId?: string) => {
    if (!subscription) return 0;

    const time = subscription.activityTracking.find(
      track => track.activity === activityId
    )?.timeTracking;

    return time ?? 0;
  };

  const updateTimeTracking = (activityId: string, time: number) => {
    updateActivityTracking({
      activity: activityId,
      timeTracking: time
    });
  };

  const getCurrentActivity = useMemo(
    () => (selectedSuscription?: ISubscription) => {
      const { course: { sections = [] } = {}, currentActivity = '' } =
        selectedSuscription ?? (subscription || {});
      const activityId = currentActivityId ?? currentActivity;

      const selectedActivity = sections.reduce((acc, section, index) => {
        const activity = section.activities.find(({ _id }) => _id === activityId);
        if (activity) {
          return {
            activity,
            section,
            index
          };
        }
        return acc;
      }, {} as ICurrentCourseState);
      if (!selectedActivity.activity && sections[0]) {
        return {
          activity: sections[0].activities[0],
          section: sections[0],
          index: 0
        };
      }
      return selectedActivity;
    },
    [currentActivityId, subscription]
  );

  const areAllActivitiesLocked = (section: ISection) => {
    return section.activities.every(
      ({ isLocked, resource }) => isLocked || (!resource?.file && !resource?.quiz)
    );
  };

  const isPlanificationFlow = searchParams.get('isPlanificationFlow') === 'true';

  const planNav = usePlanificationNavigation({
    sortedPlanificationActivitiesByDate,
    currentActivityId
  });

  const normalNav = useActivityNavigation(subscription);

  const previousActivity = isPlanificationFlow
    ? planNav.previousActivity
    : normalNav.previousActivity;
  const nextActivity = isPlanificationFlow ? planNav.nextActivity : normalNav.nextActivity;
  const navigateToPreviousActivity = isPlanificationFlow
    ? planNav.navigateToPreviousActivity
    : normalNav.navigateToPreviousActivity;
  const navigateToNextActivity = isPlanificationFlow
    ? planNav.navigateToNextActivity
    : normalNav.navigateToNextActivity;

  const updateIntroductionActivity = (activityId: string, isCompleted: boolean) => {
    updateActivityTracking(
      {
        activity: activityId,
        isCompleted
      },
      () => {
        if (!isCompleted || !subscription?._id) return;
        trackEvent({
          eventType: ETrackingEventType.INTRODUCTION_COMPLETED,
          eventData: {
            subscriptionId: subscription._id,
            courseId: subscription?.course?._id
          }
        });
      }
    );
  };

  return {
    subscription,
    isRecapModalOpen,
    showActivityReview,
    setShowActivityReview,
    setIsRecapModalOpen,
    navigateToNextActivity,
    navigateToPreviousActivity,
    getTimeTracking,
    updateTimeTracking,
    setActivityReview,
    updateActivityTracking,
    getActivityTrackById,
    setActivityAsCompleted,
    setActivityAsIncomplete,
    sendActivityProblemReport,
    getCurrentActivity,
    areAllActivitiesLocked,
    updateIntroductionActivity,
    isLoading,
    isWorking,
    currentActivityId,
    previousActivity,
    nextActivity,
    showNextActivityShortcut,
    setShowNextActivityShortcut
  };
};
